import React, { useState, useEffect } from 'react';
import { InputType } from 'reactstrap/es/Input';
import NSInput from '../NSInput/NSInput';

export interface INSProFormaInputProps {
    name: string;
    type?: InputType;
    value?: string | number | null;
    handleUpdateValue: (updatedValue: string | number) => void;
    placeholder?: string;
    disabled?: boolean;
    min?: number | string;
    max?: number | string;
    appendInputAddon?: string | JSX.Element;
    inputGroupClassName?: string;
    customClassName?: string;
    step?: number;
    includeRange?: boolean;
    rangeInputClassName?: string;
    disabledTooltip?: string;
}

const NSProFormaInput = ({
    name,
    type = 'text',
    value = null,
    handleUpdateValue,
    placeholder = '',
    disabled = false,
    min,
    max,
    appendInputAddon,
    inputGroupClassName,
    customClassName,
    step,
    includeRange = false,
    rangeInputClassName,
    disabledTooltip,
}: INSProFormaInputProps) => {
    const [inputValue, setInputValue] = useState<string>(String(value ?? ''));
    const [forceBlur, setForceBlur] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (forceBlur) {
            setTimeout(() => {
                setForceBlur(false);
            }, 1000);
        }
    }, [forceBlur]);

    useEffect(() => {
        if (!isFocused) {
            setInputValue(String(value ?? ''));
        }
    }, [value, isFocused]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        const pastedData = event.clipboardData.getData('Text');
        if (type === 'number') {
            event.preventDefault();
            const cleaned = pastedData.replace(/[\s,]+/g, '');
            // Extract leading numeric part (including optional decimal, minus, e/E)
            const match = cleaned.match(/^[-]?\d+(?:\.\d+)?(?:[eE][-]?\d+)?/);
            let result = '';

            if (match) {
                const [matchedValue] = match;
                result = matchedValue;
            } else if (/\d/.test(cleaned)) {
                // If there's at least one digit, remove non-numeric characters
                result = cleaned.replace(/[^0-9]/g, '');
            }
            setInputValue(result);
        }
    };

    const handleBlur = () => {
        const currentValue = inputValue;
        const trimmedValue = typeof currentValue === 'string' ? currentValue.trim() : currentValue;
        const valueToCompare = String(value ?? '');

        if (trimmedValue !== valueToCompare) {
            if (type === 'number') {
                handleUpdateValue(Number(trimmedValue) || 0);
            } else {
                handleUpdateValue(trimmedValue);
            }
        }
        setIsFocused(false);
    };

    const preventTypingNonNumberValues = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const allowedNonNumericKeys = ['e', 'E', '-', '.'];
        const currentValue = inputValue + e.key;

        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
            if (
                (Number.isNaN(parseFloat(currentValue)))
                || (!allowedNonNumericKeys.includes(e.key) && Number.isNaN(Number(e.key)))
            ) {
                e.preventDefault();
            }
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // Unlike Chrome, some browsers like Firefox, Safari allow typing non numeric values
        // in number inputs. This prevents such behavior.
        if (type === 'number') {
            preventTypingNonNumberValues(event);
        }
        if (event.key === 'Enter') {
            const trimmedValue = typeof inputValue === 'string' ? inputValue.trim() : inputValue;
            if (type === 'number') {
                handleUpdateValue(Number(trimmedValue) || 0);
            } else {
                handleUpdateValue(trimmedValue);
            }
            setForceBlur(true);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            setInputValue(String(value ?? ''));
            setForceBlur(true);
        }
    };

    return (
        <NSInput
            name={name}
            type={type}
            value={inputValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            placeholder={placeholder}
            disabled={disabled || forceBlur}
            min={min}
            onPaste={handlePaste}
            max={max}
            appendInputAddon={appendInputAddon}
            inputGroupClassName={inputGroupClassName}
            customClassName={customClassName}
            step={step}
            includeRange={includeRange}
            rangeInputClassName={rangeInputClassName}
            disabledTooltip={disabledTooltip}
        />
    );
};

export default NSProFormaInput;
